import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { ArrowRight, Timer, Flame, CheckCircle2, Clock, Sparkles, BookOpen, ListChecks } from "lucide-react";

const fmt = (m) => {
    const h = Math.floor(m / 60), mm = m % 60;
    return h ? `${h}h ${mm}m` : `${mm}m`;
};

function cleanMd(text) {
    if (!text) return "";
    return text
        .replace(/^#{1,6}\s+/gm, "")
        .replace(/\*\*(.*?)\*\*/g, "$1")
        .replace(/__(.*?)__/g, "$1")
        .replace(/`{1,3}([^`]*)`{1,3}/g, "$1")
        .replace(/^\s*[-*+]\s+/gm, "• ")
        .replace(/^-{3,}$/gm, "")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
}

export default function Dashboard() {
    const { user } = useAuth();
    const [summary, setSummary] = useState(null);
    const [courses, setCourses] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [insights, setInsights] = useState("");
    const [aiLoading, setAiLoading] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [s, c, a] = await Promise.all([
                    api.get("/analytics/summary"),
                    api.get("/courses"),
                    api.get("/assignments"),
                ]);
                setSummary(s.data);
                setCourses(c.data);
                setAssignments(a.data);
            } catch (e) { console.warn("[dashboard] load failed", e); }
        })();
    }, []);

    const upcoming = useMemo(() => assignments
        .filter(a => a.status !== "done" && a.due_date)
        .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
        .slice(0, 5), [assignments]);

    const askInsights = async () => {
        setAiLoading(true);
        try {
            const { data } = await api.post("/ai/insights");
            setInsights(data.insights);
        } catch (e) {
            console.warn("[dashboard] insights failed", e);
            setInsights("Couldn't fetch insights right now.");
        }
        finally { setAiLoading(false); }
    };

    const greet = (() => {
        const h = new Date().getHours();
        return h < 5 ? "Still up" : h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
    })();

    return (
        <div className="space-y-8" data-testid="dashboard">
            <header>
                <div className="mono-label">{new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</div>
                <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight mt-2">
                    {greet}, <span className="text-primary">{user?.name?.split(" ")[0]}</span>.
                </h1>
                <p className="text-muted-foreground mt-2 max-w-xl">Here's your academic command center for today.</p>
            </header>

            {/* Bento */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
                <Stat testid="stat-focus" icon={Timer} label="Focus time" value={fmt(summary?.total_focus_minutes || 0)} sub={`${summary?.total_sessions || 0} sessions`} />
                <Stat testid="stat-streak" icon={Flame} label="Study streak" value={`${summary?.streak_days || 0}d`} sub="keep it going" accent />
                <Stat testid="stat-completed" icon={CheckCircle2} label="Completed" value={summary?.completed_assignments || 0} sub={`${summary?.completion_rate || 0}% rate`} />
                <Stat testid="stat-open" icon={ListChecks} label="Open tasks" value={summary?.open_assignments || 0} sub="on your plate" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* Upcoming */}
                <section className="lg:col-span-2 bg-card rounded-2xl border border-border/60 p-6" data-testid="upcoming-list">
                    <div className="flex items-center justify-between mb-5">
                        <div>
                            <div className="mono-label">Upcoming</div>
                            <h3 className="font-display text-xl font-medium mt-1">Due soon</h3>
                        </div>
                        <Link to="/app/assignments" className="text-xs text-primary hover:underline flex items-center gap-1">See all <ArrowRight className="w-3 h-3" /></Link>
                    </div>
                    {upcoming.length === 0 ? (
                        <EmptyState title="Nothing due yet" hint="Add your first assignment to get organized." cta={<Link to="/app/assignments?new=1"><Button size="sm" data-testid="empty-new-assignment">Add assignment</Button></Link>} />
                    ) : (
                        <ul className="divide-y divide-border/50">
                            {upcoming.map(a => {
                                const c = courses.find(c => c.id === a.course_id);
                                const daysLeft = Math.ceil((new Date(a.due_date) - new Date()) / 86400000);
                                return (
                                    <li key={a.id} className="py-3 flex items-center gap-4">
                                        <span className="w-1.5 h-8 rounded-full" style={{ background: c?.color || "hsl(var(--muted))" }} />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium truncate">{a.title}</div>
                                            <div className="text-xs text-muted-foreground">{c?.name || "No course"} · {a.priority}</div>
                                        </div>
                                        <div className={`text-xs font-mono ${daysLeft < 0 ? "text-destructive" : daysLeft < 2 ? "text-accent" : "text-muted-foreground"}`}>
                                            {daysLeft < 0 ? `${Math.abs(daysLeft)}d overdue` : daysLeft === 0 ? "today" : `${daysLeft}d`}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </section>

                {/* AI insights */}
                <section className="bg-primary text-primary-foreground rounded-2xl p-6 relative overflow-hidden" data-testid="ai-insights-card">
                    <div className="grain absolute inset-0 opacity-30" />
                    <div className="relative">
                        <div className="mono-label !text-primary-foreground/60">AI insights</div>
                        <h3 className="font-display text-xl font-medium mt-1 mb-3 flex items-center gap-2"><Sparkles className="w-4 h-4 text-accent" /> Plan my day</h3>
                        {insights ? (
                            <div className="text-sm whitespace-pre-wrap leading-relaxed text-primary-foreground/95">{cleanMd(insights)}</div>
                        ) : (
                            <p className="text-sm text-primary-foreground/80 leading-relaxed mb-4">Let StudySphere AI prioritize your open assignments and craft today's study plan.</p>
                        )}
                        <Button variant="secondary" size="sm" onClick={askInsights} disabled={aiLoading} data-testid="ai-plan-my-day" className="mt-3">
                            {aiLoading ? "Thinking…" : insights ? "Refresh plan" : "Generate plan"}
                        </Button>
                    </div>
                </section>

                {/* Courses */}
                <section className="lg:col-span-3 bg-card rounded-2xl border border-border/60 p-6" data-testid="courses-preview">
                    <div className="flex items-center justify-between mb-5">
                        <div>
                            <div className="mono-label">Courses</div>
                            <h3 className="font-display text-xl font-medium mt-1">Your semester</h3>
                        </div>
                        <Link to="/app/courses" className="text-xs text-primary hover:underline flex items-center gap-1">Manage <ArrowRight className="w-3 h-3" /></Link>
                    </div>
                    {courses.length === 0 ? (
                        <EmptyState title="No courses yet" hint="Create your first course to organize assignments." cta={<Link to="/app/courses?new=1"><Button size="sm">Create course</Button></Link>} />
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {courses.filter(c => !c.archived).slice(0, 8).map(c => (
                                <Link to={`/app/assignments?course=${c.id}`} key={c.id} className="group card-hover bg-background rounded-xl border border-border/60 p-4 flex flex-col gap-2">
                                    <div className="flex items-center justify-between">
                                        <span className="w-6 h-6 rounded-md" style={{ background: c.color }} />
                                        <span className="mono-label !text-[10px]">{c.code || ""}</span>
                                    </div>
                                    <div className="font-display font-medium truncate">{c.name}</div>
                                    <div className="text-xs text-muted-foreground truncate">{c.professor || "—"}</div>
                                </Link>
                            ))}
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
}

function Stat({ icon: Icon, label, value, sub, accent, testid }) {
    return (
        <div data-testid={testid} className={`card-hover rounded-2xl border border-border/60 p-5 ${accent ? "bg-accent/10" : "bg-card"}`}>
            <div className="flex items-center justify-between mb-4">
                <div className="mono-label">{label}</div>
                <Icon className={`w-4 h-4 ${accent ? "text-accent" : "text-muted-foreground"}`} />
            </div>
            <div className="font-display text-3xl font-medium tracking-tight">{value}</div>
            <div className="text-xs text-muted-foreground mt-1">{sub}</div>
        </div>
    );
}

function EmptyState({ title, hint, cta }) {
    return (
        <div className="py-8 text-center">
            <div className="font-display text-lg mb-1">{title}</div>
            <p className="text-sm text-muted-foreground mb-4">{hint}</p>
            {cta}
        </div>
    );
}
